import {
  Flex,
  Typography,
  SafeAreaTopSpacing,
  NaviIconBack,
  IconSearchCamera,
  IconCancel,
  ColorValues,
  useScreenSize,
} from '@banhala/ui-kit';
import React from 'react';
import { TextInput } from 'react-native';
import { useHandleBackPressed } from 'shared/src/hooks';
import { isWeb } from 'shared/src/utils';
import { DESKTOP_MAX_WIDTH } from 'shared/src/constants/layout';
import { Accessibility } from '../../../utils/accessibility';
import { SearchResultCameraTooltip } from './CameraTooltip/SearchResultCameraTooltip';

type Props = {
  query: string;
  onPressSearchBar: () => void;
  onPressClear: () => void;
  onPressImageSearch: () => void;
};

export const SearchResultHeader: React.FC<Props> = ({
  query,
  onPressSearchBar,
  onPressClear,
  onPressImageSearch,
}) => {
  const { onPressBack } = useHandleBackPressed();
  const { width: screenWidth } = useScreenSize();
  const contentWidth = Math.min(screenWidth, DESKTOP_MAX_WIDTH);

  const hasQuery = query.trim().length > 0;

  return (
    // 웹: ui-kit List 와 동일한 자체 처리 패턴 (모바일 폭 가운데 정렬).
    // 모바일: undefined → 부모 폭 그대로 (Z폴드 fold/unfold 시 화면 폭 반영).
    <Flex
      backgroundColor="bg_primary"
      alignSelf={isWeb ? 'center' : undefined}
      maxWidth={isWeb ? contentWidth : undefined}
      width={isWeb ? '100%' : undefined}
    >
      <SafeAreaTopSpacing />
      <Flex
        height={48}
        padding={{ left: 2, right: 16 }}
        justifyContent="center"
      >
        <Flex.Row alignItems="center">
          {/* 뒤로가기 버튼 */}
          <Flex
            testID={Accessibility.Common.backButton}
            width={44}
            height={48}
            justifyContent="center"
            alignItems="center"
            onPress={onPressBack}
          >
            <NaviIconBack width={24} height={24} fill={'black'} />
          </Flex>

          <Flex width={2} />

          {/* 검색바 (클릭 시 SearchHelper로 이동) */}
          <Flex
            testID={Accessibility.Result.searchButton}
            flex={1}
            height={36}
            borderRadius={8}
            backgroundColor="gray20"
            justifyContent="center"
            onPress={onPressSearchBar}
          >
            <Flex
              position="absolute"
              left={12}
              right={52}
              height={36}
              justifyContent="center"
            >
              <TextInput
                testID={Accessibility.Common.searchField}
                value={query}
                editable={false}
                pointerEvents="none"
                placeholder="하나만 사도 무료배송"
                placeholderTextColor={ColorValues.gray60}
                style={{
                  padding: 0,
                  margin: 0,
                  height: 36,
                  includeFontPadding: false,
                  fontWeight: hasQuery ? 'normal' : '600',
                  color: ColorValues.black,
                }}
              />
            </Flex>

            {/* Clear 버튼 */}
            <Flex
              position="absolute"
              right={0}
              height={36}
              width={52}
              justifyContent="center"
              alignItems="center"
            >
              {hasQuery && (
                <Flex
                  testID={Accessibility.Common.clearSearchKeywordButton}
                  width={36}
                  height={36}
                  justifyContent="center"
                  alignItems="flex-end"
                  onPress={onPressClear}
                >
                  <IconCancel />
                </Flex>
              )}
            </Flex>
          </Flex>

          {!isWeb && (
            <>
              <Flex width={12} />
              {/* 이미지 검색 버튼 */}
              <Flex.Row
                alignItems="center"
                gap={4}
                onPress={onPressImageSearch}
              >
                <IconSearchCamera fill={'black'} />
                <Typography type="subtitle3" color="content_primary">
                  검색
                </Typography>
              </Flex.Row>
            </>
          )}
        </Flex.Row>
        {/* iOS: tooltip.top = searchBarView.bottom - 3, trailing = searchBarView.trailing.inset(12) */}
        {!isWeb && (
          <Flex position="absolute" top={45} right={12} zIndex={100}>
            <SearchResultCameraTooltip />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};